import "./constraintPanel.scss";

import PropTypes from "prop-types";
import React, { createRef, Fragment } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withContainerContext } from "../../components/container/ContainerContext";
import EmptyState from "../../components/emptyState";
import FormButton from "../../components/form/FormButton";
import FormSearchField from "../../components/form/FormSearchField";
import Icon from "../../components/interface/Icon";
import ListItemView from "../../components/listView/ListItemView";
import ListView from "../../components/listView/ListView";
import keyConstants from "../../constants/keyConstants";
import { appStateOperations } from "../../modules/appState";
import { displayCacheSelectors } from "../../modules/display";
import { itemOperations } from "../../modules/items";
import { uiStateSelectors } from "../../modules/uiState";
import { PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import { getLabelWithUntitled } from "../../utils/labelUtils";
import selectors from "./selectors";

class ConstraintPanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      searchText: ""
    };

    this.searchChanged = this.searchChanged.bind(this);
    this.renderSearch = this.renderSearch.bind(this);
    this.renderConstraintItems = this.renderConstraintItems.bind(this);
    this.renderMessage = this.renderMessage.bind(this);
    this.onResolveAllClick = this.onResolveAllClick.bind(this);
    this.constraintOptionRenderer = this.constraintOptionRenderer.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onDownArrow = this.onDownArrow.bind(this);
    this.getListDom = this.getListDom.bind(this);

    this._listViewDom = null;
    this._searchInputDom = createRef();
  }

  onResolveAllClick() {
    const brokenConstraints = this.props.constraintsByItem
      .filter(c => c.isViolated)
      .map(c => c.subject)
      .toList();
    this.props.onResolveAllClick(brokenConstraints);
  }

  searchChanged(searchText) {
    this.setState({ searchText });
  }

  renderSearch() {
    return (
      <div className="constraintPanelSearch">
        <FormSearchField
          //#!if iOS !== true
          autoFocus={true}
          //#!endif
          ref={this._searchInputDom}
          placeholder="Filter"
          value={this.state.searchText}
          onChange={this.searchChanged}
          onDownArrow={this.onDownArrow}
          continuouslyUpdates={true}
          showSearchIcon={true}
          showClearIcon={true}
        />
      </div>
    );
  }

  getConstraintsOptions() {
    const { constraintsByItem } = this.props;
    const { searchText } = this.state;
    const lowerSearch = searchText ? searchText.toLowerCase() : null;
    return (lowerSearch
      ? constraintsByItem.filter(
        c =>
          getLabelWithUntitled(c.label)
            .toLowerCase()
            .indexOf(lowerSearch) >= 0
      )
      : constraintsByItem
    ).toList();
  }

  getListDom(dom) {
    this._listViewDom = dom;
  }

  constraintOptionRenderer(opt, isSelected, ref) {
    const { onItemClick } = this.props;
    const leftIcon = <Icon icon={opt.typeIcon} className={opt.typeIcon} color={opt.typeIconColor} />;
    const rightIcon = (
      <Icon
        icon={opt.isViolated ? "statusConflict" : "statusValid"}
        className={opt.isViolated ? "statusConflict" : "statusValid"}
        color={opt.isViolated ? "statusError" : "statusValid"}
      />
    );

    return (
      <ListItemView
        ref={ref}
        key={opt.subject}
        text={getLabelWithUntitled(opt.label)}
        leftElements={leftIcon}
        rightElements={rightIcon}
        onClick={onItemClick}
        clickContext={opt.subject}
        isSelected={isSelected}
      />
    );
  }

  renderConstraintItems() {
    return this.getListView();
  }

  getListView() {
    const { onItemClick, selectedItemIds } = this.props;
    const options = this.getConstraintsOptions();
    const selectedIndex = options.findIndex(c => selectedItemIds.contains(c.subject));
    const onItemIndexClick = newIndex => onItemClick(options.find((item, index) => index === newIndex).subject);

    return (
      <ListView
        items={options}
        selectedItemIndex={selectedIndex}
        onItemSelect={onItemIndexClick}
        itemRenderer={this.constraintOptionRenderer}
        isEnterToSelect={false}
        getDom={this.getListDom}
      />
    );
  }

  renderMessage() {
    const { warningCount } = this.props;

    return (
      <div className="constraintPanelMessage">
        {warningCount > 0
          ? "Select items below to manually resolve them, or click Resolve All to resolve them automatically."
          : "Select an item to view its constraints in the inspector"}
      </div>
    );
  }

  onDownArrow() {
    this._listViewDom ? this._listViewDom.focus() : null;
    this.moveHighlight(1);
  }

  onKeyDown(e) {
    // Focus the list view
    // and select the first item
    if (e.key === keyConstants.Down) {
      e.stopPropagation();
      e.preventDefault();
      this._listViewDom.focus();
      const options = this.getConstraintsOptions();
      if (options.size > 0) {
        this.props.onItemClick(options.get(0).subject);
      }
    } else {
      this._searchInputDom && this._searchInputDom.current ? this._searchInputDom.current.focus() : null;
    }
  }

  render() {
    const { warningCount, constraintsByItem } = this.props;

    return (
      <div className={`constraintPanel ${!constraintsByItem.size ? "empty" : ""}`} onKeyDown={this.onKeyDown}>
        <div className="constraintPanelHeader">
          <span>Constraints</span>
          {warningCount > 0 ? <FormButton label="Resolve All" primary={true} onClick={this.onResolveAllClick} /> : null}
        </div>
        {constraintsByItem.size ? this.renderMessage() : null}
        {constraintsByItem.size ? (
          <Fragment>
            {this.renderSearch()}
            {this.renderConstraintItems()}
          </Fragment>
        ) : (
          <EmptyState containerRect={this.props.containerRect} svg="constraint" type="sidePanel">
            <div key="content">
              <p>Looks like you haven&apos;t added any Constraints yet.</p>
            </div>
          </EmptyState>
        )}
      </div>
    );
  }
}

const mapStateToProps = state => {
  return {
    validatedDateConstraints: displayCacheSelectors.getValidatedDateConstraints(state.present),
    warningCount: selectors.getWarningsCount(state.present),
    constraintsByItem: selectors.getConstraintsByItem(state.present),
    selectedItemIds: uiStateSelectors.getSelectedItems(state.present)
  };
};

const readonlyActions = {
  onItemClick: appStateOperations.selectItems
};

const editingActions = {
  onResolveAllClick: itemOperations.resolveAllConstraints
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

ConstraintPanel.propTypes = {
  validatedDateConstraints: ImmutablePropTypes.map,
  onResolveAllClick: PropTypes.func.isRequired,
  warningCount: PropTypes.number.isRequired,
  constraintsByItem: ImmutablePropTypes.map.isRequired,
  onItemClick: PropTypes.func.isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,

  // From containerContext
  containerRect: PropTypeShapes.flatRect.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(withContainerContext(ConstraintPanel));
